/**
 * Promise-based concurrency pool.
 *
 * Limits the number of tasks running simultaneously. Tasks that exceed
 * the concurrency limit are queued and drained automatically as slots
 * become available.
 */

type Task<T> = () => Promise<T>;

interface QueueEntry {
  task: Task<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

export interface ConcurrencyPool {
  /** Submit a task. Resolves when the task completes. */
  add<T>(task: Task<T>): Promise<T>;
  /** Number of tasks waiting for a slot. */
  readonly pending: number;
  /** Number of tasks currently running. */
  readonly running: number;
}

export function createPool(concurrency: number): ConcurrencyPool {
  let running = 0;
  const queue: QueueEntry[] = [];

  function drain(): void {
    while (running < concurrency && queue.length > 0) {
      running++;
      const entry = queue.shift()!;
      entry
        .task()
        .then(entry.resolve, entry.reject)
        .finally(() => {
          running--;
          drain();
        });
    }
  }

  return {
    add<T>(task: Task<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        queue.push({
          task: task as Task<unknown>,
          resolve: resolve as (value: unknown) => void,
          reject,
        });
        drain();
      });
    },
    get pending() {
      return queue.length;
    },
    get running() {
      return running;
    },
  };
}
